'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { useSearchParams } from 'next/navigation';
import { FC, useRef } from 'react';
import { resolveFromPostLabel } from '@/components/BlogPage/AuthorHero/utils';
import SocialLinks from '@/components/BlogPage/SocialLinks';
import { BlogAuthorDto } from '@/components/BlogPage/types';
import Image from '@/components/Image';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { breadcrumbTrails } from '@/lib/breadcrumbs';
import css from './style.module.scss';
interface Props {
  author: BlogAuthorDto;
}
const AuthorHero: FC<Props> = t0 => {
  const $ = _c(30);
  const {
    author
  } = t0;
  const wrapperRef = useRef(null);
  const breadcrumbsRef = useRef(null);
  const searchParams = useSearchParams();
  const fromPostUrl = searchParams.get("from");
  const fromPost = fromPostUrl ? {
    label: resolveFromPostLabel(author, fromPostUrl),
    href: `/blog/post/${fromPostUrl}`
  } : undefined;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (!wrapperRef.current) {
        return;
      }
      const elements = [breadcrumbsRef.current, wrapperRef.current.querySelector(`.${css.leftSide}`), wrapperRef.current.querySelector(`.${css.rightSide}`)].filter(Boolean);
      gsap.fromTo(elements, {
        opacity: 0,
        y: 40
      }, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.25,
        ease: "power2.out"
      });
    };
    t2 = {
      scope: wrapperRef
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useGSAP(t1, t2);
  const t3 = css;
  const T0 = PageBreadcrumbs;
  const t4 = breadcrumbTrails.blogAuthor(author.name, fromPost);
  let t5;
  if ($[2] !== T0 || $[3] !== t4) {
    t5 = <T0 ref={breadcrumbsRef} items={t4} withContainer={false} wrapperClassName={css.breadcrumbsWrap} />;
    $[2] = T0;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== author.avatar || $[6] !== author.name) {
    t6 = author.avatar?.url ? <Image src={author.avatar.url} alt={author.name || ""} width={author.avatar.width} height={author.avatar.height} quality={100} className={css.avatar} /> : null;
    $[5] = author.avatar;
    $[6] = author.name;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== author.name) {
    t7 = <Typography variant="h3" tag="h1" className={css.name}>{author.name}</Typography>;
    $[8] = author.name;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== author.role) {
    t8 = <Typography variant="body-l" tag="p" className={css.role}>{author.role}</Typography>;
    $[10] = author.role;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <div className={css.leftSide}>{t6}{t7}{t8}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== author.description) {
    t10 = <div className={css.despription}><StrapiRichContent content={author.description} /></div>;
    $[16] = author.description;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== author.socials) {
    t11 = author.socials.length ? <div className={css.socials}><Typography variant="body-l" tag="span">Connect on:</Typography><SocialLinks socials={author.socials} /></div> : null;
    $[18] = author.socials;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = <div className={css.rightSide}>{t10}{t11}</div>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t9) {
    t13 = <div className={css.content}>{t9}{t12}</div>;
    $[23] = t12;
    $[24] = t9;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13 || $[27] !== t3.wrapper || $[28] !== t5) {
    t14 = <section ref={wrapperRef} className={t3.wrapper}>{t5}{t13}</section>;
    $[26] = t13;
    $[27] = t3.wrapper;
    $[28] = t5;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
};
export default AuthorHero;